import { isCommercial, isOutNow, isRunningLate } from './alerts.js'
import { loadOf } from './controller.js'
import { totalDriveMinutes } from './travel.js'
import type { BoardFilter, BoardVisit, SchedulablePerson } from './types.js'

/**
 * Pulse figures and chip filters — readings of the day already loaded. None of
 * them hit the server. How a block looks is `bookingLook` (decision 105).
 */

export const BOARD_FILTERS: Array<{ id: BoardFilter; label: string }> = [
  { id: 'everything', label: 'Everything' },
  { id: 'outNow', label: 'Out now' },
  { id: 'runningLate', label: 'Running late' },
  { id: 'commercial', label: 'Commercial' },
]

export function visitMatchesFilter(visit: BoardVisit, filter: BoardFilter, now = new Date()): boolean {
  switch (filter) {
    case 'everything':
      return true
    case 'outNow':
      return isOutNow(visit)
    case 'runningLate':
      return isRunningLate(visit, now)
    case 'commercial':
      return isCommercial(visit)
    case 'notGoingToPlan':
      return isRunningLate(visit, now) || visit.job.status === 'AWAITING_PARTS'
  }
}

/**
 * How much of a block's meta row fits.
 *
 * Tuned so a fifteen-minute call does not clip its own place name. Duration
 * ≥75 min, time without duration ≥45, time without the status dot ≥30, the
 * whole meta row hides below that.
 */
export type BlockMetaLevel = 'full' | 'noDuration' | 'noDot' | 'nameOnly'

export function blockMetaLevel(durationMinutes: number): BlockMetaLevel {
  if (durationMinutes >= 75) return 'full'
  if (durationMinutes >= 45) return 'noDuration'
  if (durationMinutes >= 30) return 'noDot'
  return 'nameOnly'
}

export interface BoardPulse {
  drivingMinutes: number
  longestRunMinutes: number
  recoverableMinutes: number
}

export function boardPulse(visitsByPerson: Map<string, BoardVisit[]>): BoardPulse {
  const drivingMinutes = [...visitsByPerson.values()].reduce(
    (sum, rows) => sum + totalDriveMinutes(rows),
    0,
  )

  let longestRunMinutes = 0
  for (const rows of visitsByPerson.values()) {
    const booked = loadOf(rows)
    if (booked > longestRunMinutes) longestRunMinutes = booked
  }

  return {
    drivingMinutes,
    longestRunMinutes,
    recoverableMinutes: 0,
  }
}

export function staffFlag(person: SchedulablePerson): string | null {
  const role = person.role?.name ?? ''
  if (/apprentice/i.test(role)) return 'Apprentice'
  return null
}
